import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { todayJalali } from '@platform/ui-kit'
import type { HolidayMap } from '@platform/ui-kit'
import { api } from './api'

/* ------------------------------------------------------------------
   تعطیلات رسمی — برای رنگی‌کردنِ انتخابگرهای تاریخ
   ------------------------------------------------------------------
   🔴 چرا اینجاست و نه در ماژول تقویم:

   تعطیلات را چند ماژول می‌خواهند (مرخصی، جلسه، حضورغیاب) ولی صاحبِ
   داده ماژول تقویم است. اگر هر ماژول `calendarApi` را مستقیم صدا
   می‌زد، به درونِ ماژولِ دیگر وابسته می‌شد — همان چیزی که در Backend
   با RULE-01/02 ممنوع است.

   ⚠️ این فقط **خواندن** است و عمداً هیچ‌چیزِ نوشتنی ندارد. ویرایشِ
   تقویم کارِ صفحهٔ تقویم سازمانی است.

   ------------------------------------------------------------------
   ⚠️ خطا بی‌صدا رد می‌شود و `Map` خالی برمی‌گردد.

   تعطیلات یک کمکِ نمایشی است. اگر تقویم در دسترس نبود، فرمی که از این
   استفاده می‌کند باید کارش را بکند — نه اینکه با خطا بایستد. محاسبهٔ
   واقعیِ روزهای مرخصی هم سمتِ Backend انجام می‌شود و به این وابسته
   نیست.
   ------------------------------------------------------------------ */

/** یک روزِ تقویم — همان چیزی که `GET /calendar/years/{year}` می‌دهد. */
interface CalendarDayRow {
  date: string
  is_holiday: boolean
  title: string | null
}

interface Envelope<T> {
  data: T
}

async function fetchYear(year: number): Promise<CalendarDayRow[]> {
  const { data } = await api.get<Envelope<CalendarDayRow[]>>(`/calendar/years/${year}`)

  return data.data
}

/**
 * تعطیلات رسمیِ سالِ جاری و سالِ بعد — آمادهٔ دادن به `JalaliDatePicker`.
 *
 * ⚠️ دو سال و نه یکی: بازه‌ای که از اسفند به فروردینِ سال بعد می‌رود
 * وگرنه نیمی از تقویمش بی‌رنگ می‌ماند.
 */
export function useHolidays(): HolidayMap {
  /* ⚠️ یک بار در عمرِ کامپوننت — نه در هر رندر. */
  const year = useMemo(() => todayJalali().jy, [])

  const query = useQuery({
    queryKey: ['calendar', 'holidays', year],
    queryFn: async () => {
      const years = await Promise.all([fetchYear(year), fetchYear(year + 1)])

      return years.flat()
    },
    /* تقویمِ رسمی در طولِ یک نشست عوض نمی‌شود. */
    staleTime: 60 * 60 * 1000,
    /*
     | ⚠️ تلاشِ دوباره نمی‌کند: اگر کاربر دسترسیِ تقویم را نداشته باشد
     | پاسخ ۴۰۳ است و سه بار تکرارش فقط سه درخواستِ بی‌فایده است.
     */
    retry: false,
  })

  return useMemo(() => {
    const map = new Map<string, string | null>()

    for (const day of query.data ?? []) {
      /*
       | ⚠️ فقط `is_holiday` و نه `!is_working`: پنجشنبهٔ نیمه‌وقت یا
       | روزِ غیرکاریِ یک شیفت، «تعطیل رسمی» نیست. قرمزکردنشان یعنی
       | گفتنِ چیزی که تقویم سازمانی نگفته.
       */
      if (day.is_holiday) map.set(day.date, day.title)
    }

    return map
  }, [query.data])
}
